import React, { useEffect, useState, Fragment } from "react";
import { useAuth } from "./contexts/AuthContext";
import { ENDPOINTS } from "./endpoints";
import { temPermissao } from "./utils/permissoes";

// --- INÍCIO CRUD DE DEPARTAMENTOS ---

type Departamento = {
  id: number;
  descricao: string;
  whatsapp: string;
};

type DepartamentoModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  form: Partial<Departamento>;
  setForm: React.Dispatch<React.SetStateAction<Partial<Departamento>>>;
  toast: { open: boolean; message: string; type?: 'error' | 'success' };
  descricaoRef: React.RefObject<HTMLInputElement | null>;
  editando: Departamento | null;
};

type DepartamentoModalConfirmProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  descricao?: string;
};

function DepartamentoModal({ open, onClose, onSave, form, setForm, toast, descricaoRef, editando }: DepartamentoModalProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
      <div className="modal-anim bg-white rounded-lg shadow-2xl p-4 sm:p-8 max-w-[95vw] sm:max-w-[400px] w-full">
        <h3 className="text-xl font-bold mb-4 text-gray-900">{editando ? "Editar Departamento" : "Novo Departamento"}</h3>
        <div className="w-full flex flex-col gap-3">
          <label className="text-sm font-semibold text-gray-700">ID</label>
          <input className="w-full rounded px-2 py-1 border border-gray-300 bg-gray-100 text-gray-900" value={editando?.id || ''} readOnly />
          <label className="text-sm font-semibold text-gray-700">
            Descrição <span style={{color: 'red'}}>*</span>
          </label>
          <input
            ref={descricaoRef}
            autoFocus
            className={`w-full rounded px-2 py-1 border text-gray-900 ${!form.descricao && toast.open && toast.message.includes('obrigatório') ? 'border-red-500' : 'border-gray-300'}`}
            value={form.descricao || ''}
            onChange={e => setForm((f) => ({ ...f, descricao: e.target.value }))}
            placeholder="Digite o nome do departamento"
          />
          {!form.descricao && toast.open && toast.message.includes('obrigatório') && (
            <span className="text-red-500 text-xs mt-1">Campo obrigatório</span>
          )}
          <label className="text-sm font-semibold text-gray-700">WhatsApp</label>
          <input className="w-full rounded px-2 py-1 border border-gray-300 text-gray-900" value={form.whatsapp || ''} onChange={e => setForm((f) => ({ ...f, whatsapp: e.target.value }))} placeholder="Digite o WhatsApp" />
        </div>
        <div className="flex gap-4 mt-6 justify-center">
          <button onClick={onSave} className="bg-green-600 text-white px-6 py-2 rounded font-semibold hover:bg-green-700">Salvar</button>
          <button onClick={onClose} className="bg-red-500 text-white px-6 py-2 rounded font-semibold hover:bg-red-600">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function DepartamentoModalConfirm({ open, onClose, onConfirm, descricao }: DepartamentoModalConfirmProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
      <div className="modal-anim bg-white rounded-lg shadow-2xl p-4 sm:p-8 max-w-[95vw] sm:max-w-[400px] w-full flex flex-col items-center">
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" className="mb-4">
          <circle cx="40" cy="40" r="38" stroke="#f87171" strokeWidth="4" fill="#fff" />
          <path d="M28 28L52 52" stroke="#f87171" strokeWidth="4" strokeLinecap="round" />
          <path d="M52 28L28 52" stroke="#f87171" strokeWidth="4" strokeLinecap="round" />
        </svg>
        <h3 className="text-xl font-bold mb-2">Confirma exclusão?</h3>
        <p className="text-gray-600 mb-2 text-center">Deseja realmente excluir o departamento <b>{descricao}</b>?</p>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-6 w-full justify-center items-center">
          <button
            onClick={onConfirm}
            className="bg-red-600 text-white px-6 py-2 rounded font-semibold hover:bg-red-700 transition-colors w-full sm:w-auto shadow"
          >
            Excluir
          </button>
          <button
            onClick={onClose}
            className="bg-gray-500 text-white px-6 py-2 rounded font-semibold hover:bg-gray-600 transition-colors w-full sm:w-auto shadow"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DepartamentoCRUD() {
  const { loginUnidade, permissoes } = useAuth();
  const [dados, setDados] = useState<Departamento[]>([]);
  const [filtro, setFiltro] = useState({ id: "", descricao: "", whatsapp: "" });
  const [editando, setEditando] = useState<Departamento | null>(null);
  const [novo, setNovo] = useState(false);
  const [form, setForm] = useState<Partial<Departamento>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmaExcluir, setConfirmaExcluir] = useState<Departamento | null>(null);
  const descricaoRef = React.useRef<HTMLInputElement>(null);
  const [sort, setSort] = useState<{ coluna: 'id' | 'descricao'; direcao: 'asc' | 'desc' }>({ coluna: 'id', direcao: 'asc' });
  const [toast, setToast] = useState<{ open: boolean, message: string, type?: 'error' | 'success' }>({ open: false, message: '', type: 'error' });

  // Verificações de permissão
  const podeAcessar = temPermissao(permissoes, "departamentos", "acessar_modulo");
  const podeNovo = temPermissao(permissoes, "departamentos", "novo_departamento");
  const podeAlterar = temPermissao(permissoes, "departamentos", "alterar_departamento");
  const podeExcluir = temPermissao(permissoes, "departamentos", "excluir_departamento");

  useEffect(() => {
    if (!podeAcessar) return;
    async function fetchDepartamentos() {
      if (!loginUnidade) return;
      try {
        const response = await fetch(ENDPOINTS.DEPARTAMENTO_LIST, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ unidade: loginUnidade.toLowerCase() })
        });
        const data = await response.json();
        setDados(Array.isArray(data) ? data : []);
      } catch {
        setDados([]);
      }
    }
    fetchDepartamentos();
  }, [loginUnidade, podeAcessar]);

  if (!podeAcessar) {
    return <div className="p-8 text-center text-red-600 font-bold text-xl">Você não tem permissão para acessar este módulo.</div>;
  }

  function handleEdit(dep: Departamento) {
    setEditando(dep);
    setForm(dep);
    setNovo(false);
    setTimeout(() => descricaoRef.current?.focus(), 100);
  }
  function handleDelete(dep: Departamento) {
    setConfirmaExcluir(dep);
  }
  async function handleDeleteConfirm() {
    if (confirmaExcluir) {
      try {
        const response = await fetch(ENDPOINTS.DEPARTAMENTO_DELETE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            unidade: (loginUnidade || '').toLowerCase(),
            id: confirmaExcluir.id
          })
        });
        const data = await response.json();
        if (Array.isArray(data) && data[0]?.success) {
          setDados(dados.filter(d => d.id !== confirmaExcluir.id));
          setMsg("Departamento removido!");
        } else {
          setMsg("Erro ao remover departamento!");
        }
      } catch {
        setMsg("Erro de conexão ao remover!");
      }
      setTimeout(() => setMsg(null), 2000);
      setConfirmaExcluir(null);
    }
  }
  async function handleSave() {
    if (!form.descricao) {
      setToast({ open: true, message: 'Preencha todos os campos obrigatórios!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      return;
    }
    if (novo) {
      try {
        const response = await fetch(ENDPOINTS.DEPARTAMENTO_CREATE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            unidade: (loginUnidade || '').toLowerCase(),
            descricao: form.descricao,
            whatsapp: form.whatsapp || ""
          })
        });
        const data = await response.json();
        if (Array.isArray(data) && data[0]?.success) {
          setDados([{ id: Math.max(0, ...dados.map(d => d.id)) + 1, descricao: form.descricao!, whatsapp: form.whatsapp || "" }, ...dados]);
          setMsg("Departamento adicionado!");
          setEditando(null);
          setNovo(false);
          setForm({});
        } else {
          setMsg("Erro ao adicionar departamento!");
        }
      } catch {
        setMsg("Erro de conexão ao adicionar!");
      }
      setTimeout(() => setMsg(null), 2000);
    } else if (editando) {
      try {
        const payload = {
          unidade: (loginUnidade || '').toLowerCase(),
          id: editando.id,
          descricao: form.descricao,
          whatsapp: form.whatsapp || ""
        };
        const response = await fetch(ENDPOINTS.DEPARTAMENTO_UPDATE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (Array.isArray(data) && data[0]?.success) {
          setDados(dados.map(d => d.id === editando.id ? { ...d, descricao: form.descricao!, whatsapp: form.whatsapp || "" } : d));
          setMsg("Departamento atualizado!");
          setEditando(null);
          setNovo(false);
          setForm({});
        } else {
          setMsg("Erro ao atualizar departamento!");
        }
      } catch {
        setMsg("Erro de conexão ao atualizar!");
      }
      setTimeout(() => setMsg(null), 2000);
    }
  }
  function handleCancel() {
    setEditando(null);
    setNovo(false);
    setForm({});
  }
  function handleAdd() {
    setNovo(true);
    setEditando(null);
    setForm({ descricao: "", whatsapp: "" });
    setTimeout(() => descricaoRef.current?.focus(), 100);
  }

  // Filtro aplicado + ordenação
  const dadosFiltrados = [...dados].filter(dep =>
    (filtro.id === "" || dep.id.toString().includes(filtro.id)) &&
    (filtro.descricao === "" || dep.descricao.toLowerCase().includes(filtro.descricao.toLowerCase())) &&
    (filtro.whatsapp === "" || (dep.whatsapp || "").includes(filtro.whatsapp))
  ).sort((a, b) => {
    if (sort.coluna === 'id') {
      return sort.direcao === 'asc' ? a.id - b.id : b.id - a.id;
    } else {
      const descA = a.descricao.toLowerCase();
      const descB = b.descricao.toLowerCase();
      if (descA < descB) return sort.direcao === 'asc' ? -1 : 1;
      if (descA > descB) return sort.direcao === 'asc' ? 1 : -1;
      return 0;
    }
  });

  return (
    <div className="w-full mt-4 bg-white rounded-lg shadow p-2 sm:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Departamentos</h2>
        <button
          onClick={handleAdd}
          className="bg-blue-600 text-white px-6 py-2 rounded font-semibold hover:bg-blue-700"
          disabled={!podeNovo}
          style={{ opacity: podeNovo ? 1 : 0.5, pointerEvents: podeNovo ? 'auto' : 'none' }}
        >
          <i className="fa fa-plus mr-2"></i>Novo Departamento
        </button>
      </div>
      {msg && <div className="mb-2 p-2 bg-green-100 text-green-800 text-center">{msg}</div>}
      {/* Cabeçalho e filtros - FORA do scroll */}
      <div className="overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-y-1 text-xs sm:text-base">
          <colgroup>
            <col style={{ width: "8%" }} />
            <col style={{ width: "40%" }} />
            <col style={{ width: "32%" }} />
            <col style={{ width: "20%" }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-100">
              <th className="p-1 sm:p-2 text-left w-12 sm:w-16 text-gray-900 cursor-pointer select-none" onClick={() => setSort(s => ({ coluna: 'id', direcao: s.coluna === 'id' && s.direcao === 'asc' ? 'desc' : 'asc' }))}>
                Id
                {sort.coluna === 'id' && (
                  <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th className="p-1 sm:p-2 text-left text-gray-900 cursor-pointer select-none" onClick={() => setSort(s => ({ coluna: 'descricao', direcao: s.coluna === 'descricao' && s.direcao === 'asc' ? 'desc' : 'asc' }))}>
                Departamento
                {sort.coluna === 'descricao' && (
                  <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th className="p-1 sm:p-2 text-left text-gray-900">WhatsApp</th>
              <th className="p-1 sm:p-2 text-center w-20 sm:w-32 text-gray-900">Ações</th>
            </tr>
            <tr className="bg-gray-50">
              <th className="p-1"><input className="w-full rounded px-1 py-1 text-xs sm:text-sm bg-[#fff9c4] text-black" value={filtro.id} onChange={e => setFiltro(f => ({ ...f, id: e.target.value }))} placeholder="Filtrar..." /></th>
              <th className="p-1"><input className="w-full rounded px-1 py-1 text-xs sm:text-sm bg-[#fff9c4] text-black" value={filtro.descricao} onChange={e => setFiltro(f => ({ ...f, descricao: e.target.value }))} placeholder="Filtrar..." /></th>
              <th className="p-1"><input className="w-full rounded px-1 py-1 text-xs sm:text-sm bg-[#fff9c4] text-black" value={filtro.whatsapp} onChange={e => setFiltro(f => ({ ...f, whatsapp: e.target.value }))} placeholder="Filtrar..." /></th>
              <th></th>
            </tr>
          </thead>
        </table>
      </div>
      {/* Corpo da tabela COM SCROLL */}
      <div className="overflow-y-auto max-h-[60vh]">
        <table className="min-w-full border-separate border-spacing-y-1 text-xs sm:text-base">
          <colgroup>
            <col style={{ width: "8%" }} />
            <col style={{ width: "40%" }} />
            <col style={{ width: "32%" }} />
            <col style={{ width: "20%" }} />
          </colgroup>
          <tbody>
            {dadosFiltrados.map((dep, idx) => (
              <Fragment key={dep.id}>
                <tr
                  className={`cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'} ${editando?.id === dep.id ? 'bg-teal-200' : ''} hover:bg-teal-200`}
                  onDoubleClick={() => handleEdit(dep)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="p-1 sm:p-2 text-gray-700 font-semibold">{dep.id}</td>
                  <td className="p-1 sm:p-2 text-gray-900">{dep.descricao}</td>
                  <td className="p-1 sm:p-2 text-gray-900">{dep.whatsapp}</td>
                  <td className="p-1 sm:p-2 flex gap-2 justify-center opacity-90 group-hover:opacity-100">
                    <button
                      onClick={() => podeAlterar && handleEdit(dep)}
                      className="p-2 rounded bg-blue-600 hover:bg-blue-700 flex items-center justify-center"
                      disabled={!podeAlterar}
                      style={{ opacity: podeAlterar ? 1 : 0.5, pointerEvents: podeAlterar ? 'auto' : 'none' }}
                      title="Editar"
                    >
                      <i className="fa fa-pen text-white"></i>
                    </button>
                    <button
                      onClick={() => podeExcluir && handleDelete(dep)}
                      className="p-2 rounded bg-red-600 hover:bg-red-700 flex items-center justify-center"
                      disabled={!podeExcluir}
                      style={{ opacity: podeExcluir ? 1 : 0.5, pointerEvents: podeExcluir ? 'auto' : 'none' }}
                      title="Excluir"
                    >
                      <i className="fa fa-trash text-white"></i>
                    </button>
                  </td>
                </tr>
              </Fragment>
            ))}
            {dadosFiltrados.length === 0 ? (
              <tr><td colSpan={4} className="text-center text-gray-400 py-8">Nenhum departamento encontrado.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {/* Mensagens de feedback */}
      {msg && (
        <div className={`fixed bottom-4 right-4 px-6 py-3 rounded shadow-lg ${msg.startsWith('Departamento') ? 'bg-green-500' : 'bg-red-500'} text-white`}>
          {msg}
        </div>
      )}
      {/* Modal de inclusão/edição */}
      <DepartamentoModal open={novo || !!editando} onClose={handleCancel} onSave={handleSave} form={form} setForm={setForm} toast={toast} descricaoRef={descricaoRef} editando={editando} />
      {/* Modal de confirmação de exclusão */}
      <DepartamentoModalConfirm open={!!confirmaExcluir} onClose={() => setConfirmaExcluir(null)} onConfirm={handleDeleteConfirm} descricao={confirmaExcluir?.descricao} />
    </div>
  );
} 